import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Editor } from '@tiptap/react';
import { Box } from '@chakra-ui/react';
import { TableMenu } from './TableMenu';

interface TableBubbleMenuProps {
  editor: Editor | null;
}

interface MenuPosition {
  top: number;
  right: number;
}

export const TableBubbleMenu: React.FC<TableBubbleMenuProps> = ({ editor }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [position, setPosition] = useState<MenuPosition>({ top: 0, right: 0 });
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const currentTableRef = useRef<HTMLElement | null>(null);
  const isResizingRef = useRef(false);

  const updatePosition = useCallback(() => {
    if (!editor) return;

    const isTableActive = editor.isActive('table');
    setIsVisible(isTableActive);

    if (isTableActive) {
      // Find the table element in the editor
      const { view } = editor;
      const { state } = view;
      const { selection } = state;

      // Get the resolved position to find the table node
      let tablePos = -1;

      // Walk up from current position to find the table
      state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
        if (node.type.name === 'table') {
          tablePos = pos;
          return false; // Stop iteration
        }
        return true;
      });

      if (tablePos >= 0) {
        // Get the DOM element for the table
        const tableDOM = view.nodeDOM(tablePos) as HTMLElement;

        if (tableDOM) {
          const tableRect = tableDOM.getBoundingClientRect();

          // Position fixed at top right of table using viewport coordinates
          setPosition({
            top: tableRect.top - 44, // 44px above table for menu height
            right: window.innerWidth - tableRect.right,
          });

          // Set up ResizeObserver for this table if it's a new table
          if (currentTableRef.current !== tableDOM) {
            // Clean up old observer
            if (resizeObserverRef.current) {
              resizeObserverRef.current.disconnect();
            }

            // Create new observer for this table
            resizeObserverRef.current = new ResizeObserver(() => {
              const rect = tableDOM.getBoundingClientRect();
              setPosition({
                top: rect.top - 44,
                right: window.innerWidth - rect.right,
              });
            });

            resizeObserverRef.current.observe(tableDOM);
            currentTableRef.current = tableDOM;
          }
        }
      }
    } else {
      // Clean up observer when table is not active
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      currentTableRef.current = null;
    }
  }, [editor]);

  // Handle column resize detection
  useEffect(() => {
    if (!editor) return;

    const editorElement = editor.view.dom;

    const isResizeHandle = (target: HTMLElement): boolean => {
      // Check for common TipTap/ProseMirror resize handle classes
      if (
        target.classList.contains('column-resize-handle') ||
        target.classList.contains('resize-cursor') ||
        target.classList.contains('selectedCell') ||
        target.hasAttribute('data-resize-handle')
      ) {
        return true;
      }

      // Check if cursor style indicates resize
      const computedStyle = window.getComputedStyle(target);
      if (
        computedStyle.cursor === 'col-resize' ||
        computedStyle.cursor === 'ew-resize'
      ) {
        return true;
      }

      // Check parent elements for resize handle
      let parent = target.parentElement;
      while (parent && parent !== editorElement) {
        if (
          parent.classList.contains('column-resize-handle') ||
          parent.classList.contains('resize-cursor')
        ) {
          return true;
        }
        parent = parent.parentElement;
      }

      return false;
    };

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Check if clicking on resize handle
      if (isResizeHandle(target)) {
        isResizingRef.current = true;
        setIsResizing(true);
        return;
      }

      // Also check if the editor has col-resize cursor (indicates resize mode)
      const editorStyle = window.getComputedStyle(editorElement);
      if (
        editorStyle.cursor === 'col-resize' ||
        editorStyle.cursor === 'ew-resize'
      ) {
        isResizingRef.current = true;
        setIsResizing(true);
      }
    };

    const handleMouseUp = () => {
      // Only reset if we were actually resizing
      if (isResizingRef.current) {
        isResizingRef.current = false;
        setIsResizing(false);
        // Update position after resize ends
        setTimeout(() => {
          updatePosition();
        }, 50);
      }
    };

    // Listen on document to catch all mousedown events
    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [editor, updatePosition]);

  useEffect(() => {
    if (!editor) return;

    // Update position on selection change
    editor.on('selectionUpdate', updatePosition);
    editor.on('transaction', updatePosition);

    // Also update on scroll and window resize for fixed positioning
    const handleScrollOrResize = () => {
      if (!isResizingRef.current) {
        updatePosition();
      }
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    // Initial check
    updatePosition();

    return () => {
      editor.off('selectionUpdate', updatePosition);
      editor.off('transaction', updatePosition);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);

      // Clean up ResizeObserver
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [editor, updatePosition]);

  // Hide menu when table is not active or when resizing
  if (!editor || !isVisible || isResizing) {
    return null;
  }

  return (
    <Box
      position="fixed"
      top={`${position.top}px`}
      right={`${position.right}px`}
      zIndex={1000}
      className="table-menu-container"
    >
      <TableMenu editor={editor} />
    </Box>
  );
};

export default TableBubbleMenu;
